// S2 · Step 1 — the PURE model behind the vertical picker (Module 02 §3.2, §4).
//
// Two jobs, both kept out of the components so they are unit-testable without a
// DOM or a network:
//
//  1. Reading `GET /v1/verticals/{key}`. The contract types the pack detail as
//     `VerticalPackDetail = { [key: string]: unknown }` on purpose (spec 02
//     §2.11 — the authoritative pack zod lives in `packages/verticals` and the
//     API serves it read-only from the bundle, so the OpenAPI shape does not
//     duplicate and drift from it). The preview drawer therefore reads it
//     DEFENSIVELY: never a cast that could lie, never a crash on a field a pack
//     version does not carry.
//
//  2. Turning `GET /v1/verticals/installs/current` into the HONEST breakdown the
//     spec demands. §3.2 is explicit that a kind whose owning module is not
//     wired is recorded `skipped` — "never a fake seed". So this model keeps the
//     three dispositions apart and refuses to let a skipped or snapshot kind
//     count as installed: `installedObjects` sums MATERIALIZED rows
//     (`seed_links`), and a skipped kind always carries the module it waits on.

import type { components } from "@assessly/api-client";

type S = components["schemas"];

export type PackSummary = S["VerticalPackSummary"];
export type CurrentInstall = S["VerticalPackInstallCurrentResponse"];
export type InstallRow = S["VerticalPackInstall"];
export type InstallStatus = InstallRow["status"];
export type SeedDisposition = S["VerticalPackInstallKind"]["disposition"];

// ---------------------------------------------------------------------------
// locale-aware pack text
// ---------------------------------------------------------------------------

/**
 * A pack's per-locale display string (`manifest.name`, `labels`, …) for the
 * active UI locale. Falls back requested → `en` → any present value, so a pack
 * that ships before a locale exists renders its name rather than a blank cell.
 * Returns `undefined` when the record carries nothing at all — the caller then
 * shows its own placeholder (never a raw key).
 */
export function localeText(
  labels: Readonly<Record<string, string>> | undefined,
  locale: string,
): string | undefined {
  if (labels === undefined) return undefined;
  const exact = labels[locale];
  if (exact !== undefined && exact.length > 0) return exact;
  const english = labels["en"];
  if (english !== undefined && english.length > 0) return english;
  for (const value of Object.values(labels)) {
    if (typeof value === "string" && value.length > 0) return value;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// defensive readers for the untyped pack detail
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function str(source: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = source?.[key];
  return typeof value === "string" ? value : undefined;
}

function num(source: Record<string, unknown> | undefined, key: string): number | undefined {
  const value = source?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function bool(source: Record<string, unknown> | undefined, key: string): boolean | undefined {
  const value = source?.[key];
  return typeof value === "boolean" ? value : undefined;
}

function strArray(source: Record<string, unknown> | undefined, key: string): string[] {
  return asArray(source?.[key]).filter((v): v is string => typeof v === "string");
}

/** A `{ en: "…", ru: "…" }` record, keeping only string values. */
function labelRecord(
  source: Record<string, unknown> | undefined,
  key: string,
): Record<string, string> {
  const raw = asRecord(source?.[key]);
  if (raw === undefined) return {};
  const out: Record<string, string> = {};
  for (const [locale, value] of Object.entries(raw)) {
    if (typeof value === "string") out[locale] = value;
  }
  return out;
}

export interface PackManifestView {
  key: string;
  version: string;
  name: Record<string, string>;
  description: Record<string, string>;
  /** The pack's suggested currency — a PROVISIONING pre-fill, not the tenant's
   * currency (§3.2: tenant currency is set once by Module 15 and immutable). */
  defaultCurrency: string | undefined;
  measuredStockUnitKinds: string[];
}

export interface PackUnitView {
  code: string;
  kind: string | undefined;
  precision: number | undefined;
  labels: Record<string, string>;
}

export interface PackStepView {
  ref: string;
  sequenceHint: number | undefined;
  labels: Record<string, string>;
  workCenterTemplateRef: string | undefined;
  setupMinutes: number | undefined;
  runSecondsPerUnit: number | undefined;
  runUnitCode: string | undefined;
  operationScrapPct: number | undefined;
  captures: string[];
  qcChecklistRef: string | undefined;
  wasteCategoryRefs: string[];
}

export interface PackQcItemView {
  key: string;
  kind: string | undefined;
  unitCode: string | undefined;
  min: number | undefined;
  max: number | undefined;
  labels: Record<string, string>;
}

export interface PackQcView {
  ref: string;
  controlPoint: string | undefined;
  labels: Record<string, string>;
  items: PackQcItemView[];
  defaultFailAction: string | undefined;
  allowGuidedCut: boolean | undefined;
}

export interface PackWasteView {
  ref: string;
  labels: Record<string, string>;
  dispositionDefault: string | undefined;
  recoverable: boolean | undefined;
  tracksDimensions: boolean | undefined;
}

export interface PackTermView {
  termKey: string;
  labels: Record<string, string>;
}

/** The five preview tabs' content (§4 S2), read out of the untyped detail. */
export interface PackDetailView {
  manifest: PackManifestView | undefined;
  units: PackUnitView[];
  steps: PackStepView[];
  qcChecklists: PackQcView[];
  wasteCategories: PackWasteView[];
  terminology: PackTermView[];
}

export function readPackDetail(detail: unknown): PackDetailView {
  const pack = asRecord(detail);
  const manifestRaw = asRecord(pack?.["manifest"]);
  const manifest: PackManifestView | undefined =
    manifestRaw === undefined
      ? undefined
      : {
          key: str(manifestRaw, "key") ?? "",
          version: str(manifestRaw, "version") ?? "",
          name: labelRecord(manifestRaw, "name"),
          description: labelRecord(manifestRaw, "description"),
          defaultCurrency: str(manifestRaw, "default_currency"),
          measuredStockUnitKinds: strArray(manifestRaw, "measured_stock_unit_kinds"),
        };

  const units = asArray(pack?.["units"]).flatMap((entry): PackUnitView[] => {
    const row = asRecord(entry);
    const code = str(row, "code");
    if (code === undefined) return [];
    return [
      {
        code,
        kind: str(row, "kind"),
        precision: num(row, "precision"),
        labels: labelRecord(row, "labels"),
      },
    ];
  });

  const steps = asArray(pack?.["routing_steps"]).flatMap((entry): PackStepView[] => {
    const row = asRecord(entry);
    const ref = str(row, "ref");
    if (ref === undefined) return [];
    return [
      {
        ref,
        sequenceHint: num(row, "sequence_hint"),
        labels: labelRecord(row, "labels"),
        workCenterTemplateRef: str(row, "work_center_template_ref"),
        setupMinutes: num(row, "default_setup_minutes"),
        runSecondsPerUnit: num(row, "default_run_seconds_per_unit"),
        runUnitCode: str(row, "run_unit_code"),
        operationScrapPct: num(row, "operation_scrap_pct"),
        captures: strArray(row, "captures"),
        qcChecklistRef: str(row, "qc_checklist_ref"),
        wasteCategoryRefs: strArray(row, "waste_category_refs"),
      },
    ];
  });
  steps.sort((a, b) => (a.sequenceHint ?? 0) - (b.sequenceHint ?? 0));

  const qcChecklists = asArray(pack?.["qc_checklists"]).flatMap((entry): PackQcView[] => {
    const row = asRecord(entry);
    const ref = str(row, "ref");
    if (ref === undefined) return [];
    return [
      {
        ref,
        controlPoint: str(row, "control_point"),
        labels: labelRecord(row, "labels"),
        defaultFailAction: str(row, "default_fail_action"),
        allowGuidedCut: bool(row, "allow_guided_cut"),
        items: asArray(row?.["items"]).flatMap((item): PackQcItemView[] => {
          const cell = asRecord(item);
          const key = str(cell, "key");
          if (key === undefined) return [];
          return [
            {
              key,
              kind: str(cell, "kind"),
              unitCode: str(cell, "unit_code"),
              min: num(cell, "min"),
              max: num(cell, "max"),
              labels: labelRecord(cell, "labels"),
            },
          ];
        }),
      },
    ];
  });

  const wasteCategories = asArray(pack?.["waste_categories"]).flatMap((entry): PackWasteView[] => {
    const row = asRecord(entry);
    const ref = str(row, "ref");
    if (ref === undefined) return [];
    return [
      {
        ref,
        labels: labelRecord(row, "labels"),
        dispositionDefault: str(row, "disposition_default"),
        recoverable: bool(row, "recoverable"),
        tracksDimensions: bool(row, "tracks_dimensions"),
      },
    ];
  });

  const terminology = asArray(pack?.["terminology"]).flatMap((entry): PackTermView[] => {
    const row = asRecord(entry);
    const termKey = str(row, "term_key");
    if (termKey === undefined) return [];
    return [{ termKey, labels: labelRecord(row, "labels") }];
  });

  return { manifest, units, steps, qcChecklists, wasteCategories, terminology };
}

// ---------------------------------------------------------------------------
// install state
// ---------------------------------------------------------------------------

/**
 * What the screen shows. `none` is the legitimate "never installed" state (the
 * API answers 404 PACK_INSTALL_NOT_FOUND and the query hook maps it to `null`),
 * NOT an error. `superseded` means the only row left is a switched-away pack —
 * the tenant currently has no active pack, so the picker behaves like `none`
 * while still naming what was superseded.
 */
export type InstallPhase = "none" | "installing" | "installed" | "failed" | "superseded";

export function installPhase(current: CurrentInstall | null | undefined): InstallPhase {
  if (current === null || current === undefined) return "none";
  return current.install.status;
}

/** The pack key currently held by an ACTIVE install (installing or installed).
 * A failed/superseded row does not own the tenant, so it returns undefined. */
export function activePackKey(current: CurrentInstall | null | undefined): string | undefined {
  if (current === null || current === undefined) return undefined;
  const { status, pack_key } = current.install;
  return status === "installing" || status === "installed" ? pack_key : undefined;
}

/** True when installing this key would need `force=true` (§3.2 pt 4 switch). */
export function needsForce(current: CurrentInstall | null | undefined, key: string): boolean {
  const active = activePackKey(current);
  return active !== undefined && active !== key;
}

/** True when this key IS the active install — the card renders "installed",
 * never an install button that the API would answer 409 to. */
export function isActivePack(current: CurrentInstall | null | undefined, key: string): boolean {
  return activePackKey(current) === key;
}

export interface InstallErrorView {
  code: string | undefined;
  messageKey: string | undefined;
  /** `error.detail` is an object per §2.3; its `message` is the human part. */
  detail: string | undefined;
}

/** Read `vertical_pack_installs.error` (`{code, message_key, detail:{message}}`). */
export function installError(install: InstallRow): InstallErrorView | undefined {
  const error = asRecord(install.error);
  if (error === undefined) return undefined;
  const detailObj = asRecord(error["detail"]);
  return {
    code: str(error, "code"),
    messageKey: str(error, "message_key"),
    detail: str(detailObj, "message") ?? str(error, "detail"),
  };
}

/**
 * One row of the per-kind breakdown (§3.2). `packCount` is the PACK CARDINALITY
 * (how many objects the pack declares) and `materialized` is how many rows
 * actually exist in an owning module (`pack_seed_links`). They are deliberately
 * separate numbers: for a `skipped` kind `materialized` is always 0 and
 * `ownerModule` names what the seed is waiting on, so the UI can say "pending
 * <module>" instead of implying an install — and it says it through
 * `t('…breakdown.pendingModule', { module })`, in the reader's language. There
 * is no `reason` field: the API used to send an English sentence saying that,
 * this model mapped it, and no screen ever rendered it (D-225).
 */
export interface KindRow {
  objectKind: string;
  disposition: SeedDisposition;
  packCount: number;
  /** Materialized owning-module rows. `null` for `snapshot` kinds, which
   * legitimately create none (they are served from `pack_snapshot`, §2.4). */
  materialized: number | null;
  ownerModule: string;
}

export function kindRows(current: CurrentInstall): KindRow[] {
  const linked = new Map<string, number>();
  for (const link of current.seed_links) linked.set(link.object_kind, link.count);
  return current.kinds
    .filter((kind) => kind.count > 0)
    .map((kind) => ({
      objectKind: kind.object_kind,
      disposition: kind.disposition,
      packCount: kind.count,
      materialized: kind.disposition === "snapshot" ? null : (linked.get(kind.object_kind) ?? 0),
      ownerModule: kind.owner_module,
    }));
}

export interface KindGroups {
  seeded: KindRow[];
  snapshot: KindRow[];
  skipped: KindRow[];
}

export function groupKinds(rows: readonly KindRow[]): KindGroups {
  return {
    seeded: rows.filter((r) => r.disposition === "seeded"),
    snapshot: rows.filter((r) => r.disposition === "snapshot"),
    skipped: rows.filter((r) => r.disposition === "skipped"),
  };
}

export interface InstallTotals {
  /** Objects the pack declares across every kind. */
  declared: number;
  /** Objects a seeding handler is expected to materialize. */
  expected: number;
  /** Objects that ACTUALLY exist in an owning module right now. */
  materialized: number;
  /** Objects held in `pack_snapshot` only (§2.4) — real, but not module rows. */
  snapshot: number;
  /** Objects whose owning module is not wired yet (§3.2) — NOT installed. */
  pending: number;
  /** 0–100 progress of the seeding pass; `expected === 0` ⇒ 100 (nothing to do). */
  percent: number;
}

export function installTotals(rows: readonly KindRow[]): InstallTotals {
  const groups = groupKinds(rows);
  const sum = (list: readonly KindRow[], pick: (row: KindRow) => number): number =>
    list.reduce((acc, row) => acc + pick(row), 0);

  const expected = sum(groups.seeded, (r) => r.packCount);
  const materialized = sum(groups.seeded, (r) => r.materialized ?? 0);
  return {
    declared: sum(rows, (r) => r.packCount),
    expected,
    materialized: Math.min(materialized, expected),
    snapshot: sum(groups.snapshot, (r) => r.packCount),
    pending: sum(groups.skipped, (r) => r.packCount),
    percent: expected === 0 ? 100 : Math.min(100, Math.round((materialized / expected) * 100)),
  };
}

/** Pack kinds worth showing on a card, biggest first, for the "what you get"
 * one-liner. `counts_by_kind` is keyed by the plural kind name (§2.3). */
export function topCounts(
  counts: Readonly<Record<string, number>>,
  limit: number,
): { kind: string; count: number }[] {
  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .map(([kind, count]) => ({ kind, count }))
    .sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind))
    .slice(0, limit);
}

/** Total objects a pack declares — the honest headline number on a card. */
export function totalCount(counts: Readonly<Record<string, number>>): number {
  return Object.values(counts).reduce((acc, n) => acc + n, 0);
}
